import { useEffect, useState } from 'react';
import type { ServiceCode } from '@common/enums/serviceCode';
import type { IAuthorization } from '@frontend/components/interfaces/authorization';
import type { IServiceProvider } from '@frontend/components/interfaces/serviceProvider';
import { ManagerAuthorization } from '@common/enums';
import { ServiceType } from '@common/enums/serviceType';
import { getAnalystAllowedByServiceCode } from '@common/utils/getAnalystAllowedByServiceCode';
import {
    AdminTable,
    DeleteButton,
    EditButton,
    FullName,
    Header,
    Link,
    LinkButton,
    TableBody,
    TableData,
    TableHead,
    TableHeader,
    TableRow,
} from '@frontend/components/elements/common';
import { NullableFormattedDate } from '@frontend/components/elements/NullableFormattedDate';
import { fetchJson } from '@frontend/store/fetch';
import { translateKey } from '@frontend/translate';
import { assertHasAuthorization } from '@frontend/utils';

interface IServiceProviderListProps {
    serviceCode: ServiceCode;
    serviceId: string;
    serviceType: string;
}

interface IAuthedServiceProvider extends IServiceProvider {
    authorization: IAuthorization;
}

export function ServiceProviderList(props: IServiceProviderListProps) {
    const { serviceCode, serviceId, serviceType } = props;

    const [serviceProviders, setServiceProviders] = useState<IAuthedServiceProvider[]>([]);

    assertHasAuthorization(ManagerAuthorization.serviceRightsCareproviders);

    useEffect(() => {
        searchServiceProviders(serviceId);
    }, [serviceId]);

    return (
        <>
            <Header
                actionButtons={[
                    <LinkButton
                        icon="icon icon-add"
                        to={`/serviceProviders/add?serviceId=${serviceId}`}
                    >
                        {translateKey('common.addItem', translateKey('serviceProvider.serviceProvider'))}
                    </LinkButton>,
                ]}
                content={translateKey('serviceProvider.serviceProviders')}
            />
            <AdminTable>
                <TableHead>
                    <TableRow>
                        <TableHeader>{translateKey('organization.organization')}</TableHeader>
                        <TableHeader>{translateKey('practitioner.practitioner')}</TableHeader>
                        {serviceType === ServiceType.examination && (
                            <>
                                <TableHeader>{translateKey('serviceProvider.grader')}</TableHeader>
                                <TableHeader>{translateKey('serviceProvider.biometrician')}</TableHeader>
                                {getAnalystAllowedByServiceCode(serviceCode) && (
                                    <TableHeader>{translateKey('serviceProvider.analyst')}</TableHeader>
                                )}
                            </>
                        )}
                        <TableHeader>{translateKey('common.startDate')}</TableHeader>
                        <TableHeader>{translateKey('common.endDate')}</TableHeader>
                        <TableHeader align="right"></TableHeader>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {serviceProviders.map((serviceProvider) => (
                        <TableRow key={serviceProvider.id}>
                            <TableData>
                                <Link to={`/organizations/${serviceProvider.employment.organization.id}?tab=providers`}>
                                    {serviceProvider.employment.organization.name}
                                </Link>
                            </TableData>
                            <TableData>
                                <Link
                                    to={`/practitioners/${serviceProvider.employment.practitioner.id}?tab=servicesProvidingListIsVisible`}
                                >
                                    <FullName {...serviceProvider.employment.practitioner} />
                                </Link>
                            </TableData>
                            {serviceType === ServiceType.examination && (
                                <>
                                    <TableData>
                                        {serviceProvider.isGrader
                                            ? translateKey('common.yes')
                                            : translateKey('common.no')}
                                    </TableData>
                                    <TableData>
                                        {serviceProvider.isBiometrician
                                            ? translateKey('common.yes')
                                            : translateKey('common.no')}
                                    </TableData>
                                    {getAnalystAllowedByServiceCode(serviceCode) && (
                                        <>
                                            <TableData>
                                                {serviceProvider.isAnalyst
                                                    ? translateKey('common.yes')
                                                    : translateKey('common.no')}
                                            </TableData>
                                        </>
                                    )}
                                </>
                            )}
                            <TableData>
                                <NullableFormattedDate value={serviceProvider.startDate} />
                            </TableData>
                            <TableData>
                                <NullableFormattedDate value={serviceProvider.endDate} />
                            </TableData>

                            <TableData align="right">
                                {serviceProvider.authorization.canUpdate && (
                                    <EditButton to={`/serviceProviders/${serviceProvider.id}?serviceId=${serviceId}`} />
                                )}
                                {serviceProvider.authorization.canUpdate && serviceProvider.endDate === null && (
                                    <DeleteButton onClick={async () => await remove(serviceProvider.id)} />
                                )}
                            </TableData>
                        </TableRow>
                    ))}
                </TableBody>
            </AdminTable>
        </>
    );

    async function searchServiceProviders(sid: string) {
        const autorizationQuery = 'includeItemAuthorizations=canUpdate';
        const url = `/api/serviceProviders?serviceId=${sid}&${autorizationQuery}`;

        const result = await fetchJson(url, 'GET');
        if (result.json.items) {
            setServiceProviders(result.json.items);
        }
    }

    async function remove(id: string) {
        const url = `/api/serviceProviders/${id}`;

        await fetchJson(url, 'POST', { endDateIsCurrentDate: true });

        searchServiceProviders(serviceId);
    }
}
